const DISPLAY_LOCALE_BY_LANGUAGE_PREFIX: Record<string, string> = {
  ar: "ar",
  en: "en-US",
  it: "it-IT",
  tr: "tr-TR",
};

export function resolveDisplayLocale(languageCode?: string): string | undefined {
  const normalized = String(languageCode || "").trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  const languagePrefix = normalized.split(/[-_]/)[0] || normalized;
  const displayLocale = DISPLAY_LOCALE_BY_LANGUAGE_PREFIX[languagePrefix];
  if (displayLocale) {
    return displayLocale;
  }
  return normalized;
}

export interface VocabularyExampleTextSegment {
  text: string;
  highlighted: boolean;
}

const VOCABULARY_EXAMPLE_HIGHLIGHT_STOPWORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "by",
  "for",
  "from",
  "he",
  "her",
  "his",
  "i",
  "in",
  "is",
  "it",
  "me",
  "my",
  "of",
  "on",
  "or",
  "our",
  "she",
  "that",
  "the",
  "their",
  "them",
  "they",
  "this",
  "to",
  "we",
  "with",
  "you",
  "your",
]);

const ENGLISH_IRREGULAR_HIGHLIGHT_FORMS: Record<string, string[]> = {
  be: ["am", "is", "are", "was", "were", "been", "being"],
  become: ["became", "becoming"],
  begin: ["began", "begun", "beginning"],
  bring: ["brought", "bringing"],
  build: ["built", "building"],
  buy: ["bought", "buying"],
  catch: ["caught", "catching"],
  choose: ["chose", "chosen", "choosing"],
  come: ["came", "coming"],
  do: ["does", "did", "done", "doing"],
  fall: ["fell", "fallen", "falling"],
  feel: ["felt", "feeling"],
  find: ["found", "finding"],
  get: ["got", "gotten", "getting"],
  give: ["gave", "given", "giving"],
  go: ["goes", "went", "gone", "going"],
  grow: ["grew", "grown", "growing"],
  have: ["has", "had", "having"],
  hear: ["heard", "hearing"],
  hold: ["held", "holding"],
  keep: ["kept", "keeping"],
  know: ["knew", "known", "knowing"],
  leave: ["left", "leaving"],
  make: ["made", "making"],
  mean: ["meant", "meaning"],
  meet: ["met", "meeting"],
  put: ["putting"],
  read: ["reading"],
  say: ["says", "said", "saying"],
  see: ["saw", "seen", "seeing"],
  sell: ["sold", "selling"],
  send: ["sent", "sending"],
  sit: ["sat", "sitting"],
  speak: ["spoke", "spoken", "speaking"],
  spend: ["spent", "spending"],
  take: ["took", "taken", "taking"],
  teach: ["taught", "teaching"],
  tell: ["told", "telling"],
  think: ["thought", "thinking"],
  understand: ["understood", "understanding"],
  wake: ["woke", "woken", "waking"],
  write: ["wrote", "written", "writing"],
};

const ITALIAN_IRREGULAR_HIGHLIGHT_FORMS: Record<string, string[]> = {
  andare: ["vado", "vai", "va", "andiamo", "andate", "vanno", "andato", "andata", "andati", "andate"],
  avere: ["ho", "hai", "ha", "abbiamo", "avete", "hanno", "avuto", "avuta", "avuti", "avute"],
  dare: ["do", "dai", "da", "diamo", "date", "danno", "dato", "data", "dati", "date"],
  dire: ["dico", "dici", "dice", "diciamo", "dite", "dicono", "detto", "detta", "detti", "dette"],
  dovere: ["devo", "devi", "deve", "dobbiamo", "dovete", "devono", "dovuto", "dovuta", "dovuti", "dovute"],
  essere: ["sono", "sei", "è", "e", "siamo", "siete", "sono", "stato", "stata", "stati", "state"],
  fare: ["faccio", "fai", "fa", "facciamo", "fate", "fanno", "fatto", "fatta", "fatti", "fatte"],
  potere: ["posso", "puoi", "può", "puo", "possiamo", "potete", "possono", "potuto", "potuta", "potuti", "potute"],
  sapere: ["so", "sai", "sa", "sappiamo", "sapete", "sanno", "saputo", "saputa", "saputi", "sapute"],
  stare: ["sto", "stai", "sta", "stiamo", "state", "stanno", "stato", "stata", "stati", "state"],
  uscire: ["esco", "esci", "esce", "usciamo", "uscite", "escono", "uscito", "uscita", "usciti", "uscite"],
  vedere: ["vedo", "vedi", "vede", "vediamo", "vedete", "vedono", "visto", "vista", "visti", "viste"],
  venire: ["vengo", "vieni", "viene", "veniamo", "venite", "vengono", "venuto", "venuta", "venuti", "venute"],
  volere: ["voglio", "vuoi", "vuole", "vogliamo", "volete", "vogliono", "voluto", "voluta", "voluti", "volute"],
};

const TURKISH_IRREGULAR_VERB_HIGHLIGHT_PREFIXES: Record<string, string[]> = {
  demek: ["de", "di"],
  etmek: ["et", "ed"],
  gitmek: ["git", "gid"],
  olmak: ["ol"],
  yemek: ["ye", "yi"],
};

type VocabularyHighlightTokenMatcher = {
  exact: string[];
  prefixes: string[];
};

type VocabularyTextTokenRange = {
  start: number;
  end: number;
  variants: string[];
};

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function extractVocabularyHighlightTokens(value: string): string[] {
  return Array.from(String(value || "").matchAll(/[\p{L}\p{M}\p{N}]+(?:['’][\p{L}\p{M}\p{N}]+)?/gu))
    .map((match) => match[0])
    .filter((token) => token.length > 0);
}

function isVocabularyHighlightBoundary(value: string, index: number): boolean {
  if (index < 0 || index >= value.length) {
    return true;
  }
  return !/[\p{L}\p{M}\p{N}]/u.test(value.charAt(index));
}

function isMeaningfulVocabularyHighlightToken(token: string, tokenCount: number): boolean {
  const normalized = token.toLocaleLowerCase();
  if (tokenCount <= 1) {
    return normalized.length > 0;
  }
  return normalized.length >= 3 && !VOCABULARY_EXAMPLE_HIGHLIGHT_STOPWORDS.has(normalized);
}

function buildEnglishVocabularyHighlightForms(token: string): string[] {
  const forms = new Set<string>();
  const base = token.toLocaleLowerCase();
  if (!/^[a-z]+$/.test(base)) {
    forms.add(token);
    return Array.from(forms);
  }

  forms.add(base);
  forms.add(`${base}'s`);
  forms.add(`${base}’s`);
  for (const irregular of ENGLISH_IRREGULAR_HIGHLIGHT_FORMS[base] || []) {
    forms.add(irregular);
  }

  if (base.length >= 3) {
    const last = base.charAt(base.length - 1);
    const beforeLast = base.charAt(base.length - 2);
    const beforeBeforeLast = base.charAt(base.length - 3);
    const endsConsonantY = last === "y" && !/[aeiou]/.test(beforeLast);
    const endsSilentE = last === "e";
    const endsShortCvc =
      base.length >= 3
      && !/[aeiouwxy]/.test(last)
      && /[aeiou]/.test(beforeLast)
      && !/[aeiou]/.test(beforeBeforeLast);

    forms.add(`${base}s`);
    if (endsConsonantY) {
      forms.add(`${base.slice(0, -1)}ies`);
      forms.add(`${base.slice(0, -1)}ied`);
      forms.add(`${base}ing`);
    } else if (endsSilentE) {
      forms.add(`${base}d`);
      forms.add(`${base.slice(0, -1)}ing`);
    } else {
      forms.add(`${base}ed`);
      forms.add(`${base}ing`);
    }

    if (endsShortCvc) {
      forms.add(`${base}${last}ed`);
      forms.add(`${base}${last}ing`);
    }
  }

  return Array.from(forms);
}

function getNormalizedHighlightLanguageCode(languageCode?: string): string {
  return String(languageCode || "").trim().toLowerCase().split(/[-_]/)[0] || "";
}

function isVocabularyHighlightLanguage(languageCode: string | undefined, targetLanguage: string): boolean {
  return getNormalizedHighlightLanguageCode(languageCode) === targetLanguage;
}

function isArabicHighlightLanguage(languageCode?: string): boolean {
  return isVocabularyHighlightLanguage(languageCode, "ar");
}

function isEnglishHighlightLanguage(languageCode?: string): boolean {
  return isVocabularyHighlightLanguage(languageCode, "en");
}

function isItalianHighlightLanguage(languageCode?: string): boolean {
  return isVocabularyHighlightLanguage(languageCode, "it");
}

function isTurkishHighlightLanguage(languageCode?: string): boolean {
  return isVocabularyHighlightLanguage(languageCode, "tr");
}

function normalizeArabicHighlightValue(value: string): string {
  return value
    .replace(/\u0640/g, "")
    .replace(/[إأآٱ]/g, "ا")
    .replace(/[ؤ]/g, "و")
    .replace(/[ئ]/g, "ي")
    .replace(/[ى]/g, "ي")
    .replace(/[ة]/g, "ه");
}

function normalizeVocabularyHighlightValue(value: string, languageCode?: string): string {
  const locale = resolveDisplayLocale(languageCode);
  const raw = String(value || "").normalize("NFKC");
  const lowered = locale ? raw.toLocaleLowerCase(locale) : raw.toLocaleLowerCase();
  const withoutMarks = lowered.normalize("NFD").replace(/\p{M}/gu, "").normalize("NFC");
  return isArabicHighlightLanguage(languageCode)
    ? normalizeArabicHighlightValue(withoutMarks)
    : withoutMarks;
}

function addNormalizedHighlightValue(
  target: Set<string>,
  value: string,
  languageCode?: string,
  minLength = 1,
): void {
  const normalized = normalizeVocabularyHighlightValue(value, languageCode);
  if (normalized.length >= minLength) {
    target.add(normalized);
  }
}

function addTurkishHighlightPrefixes(
  prefixes: Set<string>,
  normalizedToken: string,
): void {
  const irregularPrefixes = TURKISH_IRREGULAR_VERB_HIGHLIGHT_PREFIXES[normalizedToken] || [];
  for (const prefix of irregularPrefixes) {
    if (prefix.length >= 2) {
      prefixes.add(prefix);
    }
  }
  if (!normalizedToken.endsWith("mak") && !normalizedToken.endsWith("mek")) {
    if (normalizedToken.length >= 3) {
      prefixes.add(normalizedToken);
    }
    return;
  }

  const stem = normalizedToken.slice(0, -3);
  if (stem.length >= 2) {
    prefixes.add(stem);
    const softening: Record<string, string> = {
      c: "c",
      k: "g",
      p: "b",
      t: "d",
    };
    const replacement = softening[stem.charAt(stem.length - 1)];
    if (replacement) {
      prefixes.add(`${stem.slice(0, -1)}${replacement}`);
    }
  }
}

function addItalianHighlightForms(
  exact: Set<string>,
  prefixes: Set<string>,
  normalizedToken: string,
): void {
  for (const form of ITALIAN_IRREGULAR_HIGHLIGHT_FORMS[normalizedToken] || []) {
    exact.add(normalizeVocabularyHighlightValue(form, "it"));
  }

  const reflexiveInfinitive = normalizedToken.match(/^(.+)(arsi|ersi|irsi)$/);
  if (reflexiveInfinitive?.[1] && reflexiveInfinitive[1].length >= 3) {
    prefixes.add(reflexiveInfinitive[1]);
  }

  const infinitive = normalizedToken.match(/^(.+)(are|ere|ire)$/);
  if (infinitive?.[1] && infinitive[1].length >= 3) {
    prefixes.add(infinitive[1]);
  }

  if (normalizedToken.length >= 3) {
    const base = normalizedToken.slice(0, -1);
    const last = normalizedToken.charAt(normalizedToken.length - 1);
    if (last === "o") {
      exact.add(`${base}a`);
      exact.add(`${base}i`);
      exact.add(`${base}e`);
    } else if (last === "a") {
      exact.add(`${base}o`);
      exact.add(`${base}i`);
      exact.add(`${base}e`);
    } else if (last === "e") {
      exact.add(`${base}i`);
    }
  }

  if (normalizedToken.length >= 4) {
    prefixes.add(normalizedToken);
  }
}

function buildVocabularyHighlightTokenMatcher(
  token: string,
  languageCode?: string,
): VocabularyHighlightTokenMatcher {
  const exact = new Set<string>();
  const prefixes = new Set<string>();
  const normalizedToken = normalizeVocabularyHighlightValue(token, languageCode);
  if (!normalizedToken) {
    return { exact: [], prefixes: [] };
  }

  exact.add(normalizedToken);

  if (isEnglishHighlightLanguage(languageCode)) {
    for (const form of buildEnglishVocabularyHighlightForms(normalizedToken)) {
      addNormalizedHighlightValue(exact, form, languageCode);
    }
    if (normalizedToken.length >= 4) {
      prefixes.add(normalizedToken);
    }
  } else if (isItalianHighlightLanguage(languageCode)) {
    addItalianHighlightForms(exact, prefixes, normalizedToken);
  } else if (isTurkishHighlightLanguage(languageCode)) {
    addTurkishHighlightPrefixes(prefixes, normalizedToken);
  } else if (isArabicHighlightLanguage(languageCode)) {
    const withoutArticle = normalizedToken.startsWith("ال")
      ? normalizedToken.slice(2)
      : normalizedToken;
    exact.add(withoutArticle);
    if (withoutArticle.length >= 3) {
      prefixes.add(withoutArticle);
    }
    if (normalizedToken.length >= 3) {
      prefixes.add(normalizedToken);
    }
  } else if (normalizedToken.length >= 4) {
    prefixes.add(normalizedToken);
  }

  return {
    exact: Array.from(exact).filter(Boolean).sort((left, right) => right.length - left.length),
    prefixes: Array.from(prefixes).filter((prefix) => prefix.length >= 2).sort((left, right) => right.length - left.length),
  };
}

function buildArabicTextTokenVariants(normalizedToken: string): string[] {
  const variants = new Set<string>([normalizedToken]);
  const cliticPrefixes = ["وال", "فال", "بال", "كال", "لل", "ال", "و", "ف", "ب", "ك", "ل"];
  for (const prefix of cliticPrefixes) {
    if (normalizedToken.startsWith(prefix) && normalizedToken.length > prefix.length + 1) {
      variants.add(normalizedToken.slice(prefix.length));
    }
  }
  return Array.from(variants).filter(Boolean);
}

function buildVocabularyTextTokenVariants(token: string, languageCode?: string): string[] {
  const normalizedToken = normalizeVocabularyHighlightValue(token, languageCode);
  if (!normalizedToken) {
    return [];
  }
  return isArabicHighlightLanguage(languageCode)
    ? buildArabicTextTokenVariants(normalizedToken)
    : [normalizedToken];
}

function collectVocabularyTextTokenRanges(
  sentenceText: string,
  languageCode?: string,
): VocabularyTextTokenRange[] {
  const ranges: VocabularyTextTokenRange[] = [];
  for (const match of sentenceText.matchAll(/[\p{L}\p{M}\p{N}]+(?:['’][\p{L}\p{M}\p{N}]+)?/gu)) {
    const matchedText = match[0];
    const start = match.index || 0;
    const variants = buildVocabularyTextTokenVariants(matchedText, languageCode);
    if (variants.length > 0) {
      ranges.push({ start, end: start + matchedText.length, variants });
    }
  }
  return ranges;
}

function doesVocabularyMatcherMatchTextToken(
  matcher: VocabularyHighlightTokenMatcher,
  textToken: VocabularyTextTokenRange,
): boolean {
  return textToken.variants.some((variant) => (
    matcher.exact.includes(variant)
    || matcher.prefixes.some((prefix) => variant.startsWith(prefix))
  ));
}

function collectLanguageAwareVocabularyHighlightRanges(
  sentenceText: string,
  vocabularyTerm: string,
  languageCode?: string,
): Array<{ start: number; end: number }> {
  const termTokens = extractVocabularyHighlightTokens(vocabularyTerm);
  if (termTokens.length === 0) {
    return [];
  }

  const textTokens = collectVocabularyTextTokenRanges(sentenceText, languageCode);
  if (textTokens.length === 0) {
    return [];
  }

  const matchers = termTokens.map((token) => buildVocabularyHighlightTokenMatcher(token, languageCode));
  const ranges: Array<{ start: number; end: number }> = [];

  if (matchers.length > 1 && textTokens.length >= matchers.length) {
    for (let startIndex = 0; startIndex <= textTokens.length - matchers.length; startIndex += 1) {
      const matchesPhrase = matchers.every((matcher, matcherIndex) => (
        doesVocabularyMatcherMatchTextToken(matcher, textTokens[startIndex + matcherIndex])
      ));
      if (matchesPhrase) {
        ranges.push({
          start: textTokens[startIndex].start,
          end: textTokens[startIndex + matchers.length - 1].end,
        });
      }
    }
  }

  for (let index = 0; index < termTokens.length; index += 1) {
    if (!isMeaningfulVocabularyHighlightToken(termTokens[index], termTokens.length)) {
      continue;
    }
    const matcher = matchers[index];
    for (const textToken of textTokens) {
      if (doesVocabularyMatcherMatchTextToken(matcher, textToken)) {
        ranges.push({ start: textToken.start, end: textToken.end });
      }
    }
  }

  return ranges;
}

function buildVocabularyHighlightRegexSources(vocabularyTerm: string): string[] {
  const term = String(vocabularyTerm || "").trim();
  if (!term) {
    return [];
  }

  const sources = new Set<string>();
  const phraseParts = term.split(/\s+/).map((part) => part.trim()).filter(Boolean);
  if (phraseParts.length > 0) {
    sources.add(phraseParts.map((part) => escapeRegex(part)).join("[\\s-]+"));
  }

  const tokens = extractVocabularyHighlightTokens(term);
  const meaningfulTokens = tokens.filter((token) => (
    isMeaningfulVocabularyHighlightToken(token, tokens.length)
  ));
  for (const token of meaningfulTokens) {
    for (const form of buildEnglishVocabularyHighlightForms(token)) {
      sources.add(escapeRegex(form));
    }
  }

  if (tokens.length > 1 && meaningfulTokens.length > 0) {
    const firstToken = tokens[0];
    const restTokens = tokens.slice(1);
    if (isMeaningfulVocabularyHighlightToken(firstToken, tokens.length) && restTokens.length > 0) {
      const firstForms = buildEnglishVocabularyHighlightForms(firstToken)
        .map((form) => escapeRegex(form));
      sources.add(`(?:${firstForms.join("|")})[\\s-]+${restTokens.map((token) => escapeRegex(token)).join("[\\s-]+")}`);
    }
  }

  return Array.from(sources).sort((left, right) => right.length - left.length);
}

function collectVocabularyHighlightRanges(
  sentenceText: string,
  vocabularyTerm: string,
  languageCode?: string,
): Array<{ start: number; end: number }> {
  const ranges: Array<{ start: number; end: number }> = collectLanguageAwareVocabularyHighlightRanges(
    sentenceText,
    vocabularyTerm,
    languageCode,
  );
  for (const source of buildVocabularyHighlightRegexSources(vocabularyTerm)) {
    let regex: RegExp;
    try {
      regex = new RegExp(source, "giu");
    } catch {
      continue;
    }
    let match: RegExpExecArray | null;
    while ((match = regex.exec(sentenceText)) !== null) {
      const matchedText = match[0];
      if (!matchedText) {
        regex.lastIndex += 1;
        continue;
      }
      const start = match.index;
      const end = start + matchedText.length;
      if (
        isVocabularyHighlightBoundary(sentenceText, start - 1)
        && isVocabularyHighlightBoundary(sentenceText, end)
      ) {
        ranges.push({ start, end });
      }
    }
  }

  return ranges
    .sort((left, right) => (left.start - right.start) || (right.end - left.end))
    .reduce<Array<{ start: number; end: number }>>((merged, range) => {
      const previous = merged[merged.length - 1];
      if (!previous || range.start > previous.end) {
        merged.push({ ...range });
        return merged;
      }
      previous.end = Math.max(previous.end, range.end);
      return merged;
    }, []);
}

export function buildVocabularyExampleTextSegments(
  sentenceText: string,
  vocabularyTerm: string,
  languageCode?: string,
): VocabularyExampleTextSegment[] {
  const text = String(sentenceText || "");
  if (!text) {
    return [];
  }
  const ranges = collectVocabularyHighlightRanges(text, vocabularyTerm, languageCode);
  if (ranges.length === 0) {
    return [{ text, highlighted: false }];
  }

  const segments: VocabularyExampleTextSegment[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) {
      segments.push({ text: text.slice(cursor, range.start), highlighted: false });
    }
    segments.push({ text: text.slice(range.start, range.end), highlighted: true });
    cursor = range.end;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), highlighted: false });
  }
  return segments;
}
